
import { Component, Prop, Vue } from 'vue-property-decorator';
import ChatMedia from './ChatMedia.vue';
import AlternativeActionsMessage from './AlternativeActionsMessage.vue';
import { ArticleExpandedEvent, BaseEvent, BaseMessage, BaseSystemMessage, NoneOfTheAboveEvent, RollbackEvent, SystemArticleMessage, SystemOption, SystemOptionEvent } from '@/common/bot/types';
import { ItemType, getInteractivePart, OptionUtils } from '@/common/bot/lib';
import { MessageType, SystemEvent, MemberType } from '@/common/bot/enum';
import { identifyUrls, sanitizeHTML } from '@/common/utils';
import ResolutionMessage from './Resolution/ResolutionMessage.vue';
import ArticleListWithOtherNavigation from './ArticleListWithOtherNavigation.vue';
import ArticleCheckFailureMessage from './ArticleCheckFailureMessage.vue';
import PreSupportConfirmation from './PreSupportConfirmation.vue';
import PreSupportPrediction from './PreSupportPrediction.vue';
import PreSupportPromptMessage from './PreSupportPromptMessage.vue';
import RequestUserTransactionMessage from './RequestUserTransactionMessage.vue';
import AiResponseForUserMessage from './AiResponseForUserMessage.vue';
import { removeMessagePrefix } from '@/common/bot/flow-frameworks/messages';

@Component({
  components: {
  ChatMedia,
  ResolutionMessage,
  AlternativeActionsMessage,
  ArticleListWithOtherNavigation,
  ArticleCheckFailureMessage,
  PreSupportConfirmation,
  PreSupportPromptMessage,
  RequestUserTransactionMessage,
  PreSupportPrediction,
  AiResponseForUserMessage,
  },
  })
export default class ChatMessageSwitch extends Vue {
  @Prop({ default: '' }) readonly addClass!: string;
  @Prop({ default: () => ({}) }) readonly message!: BaseMessage;

  readonly MessageType = MessageType;
  readonly MemberType = MemberType;

  get isArticleWithOtherNavigation () {
    return this.message.type === MessageType.ARTICLE_LIST_WITH_OTHER_NAVIGATION;
  }

  get isPreSupportPrompt () {
    if (!ItemType.isPreSupportPromptMessage(this.message)) return {};
    const { type: messageType } = this.message;
    if (MessageType.PRE_SUPPORT_PROMPT_SUPPORT_ONLY === messageType) return { supportOnly: true };
    return { general: true };
  }

  get isPreSupportPrediction () {
    return ItemType.isPreSupportPredictionMessage(this.message);
  }

  get isArticleCheckFailedMessage () {
    return ItemType.isArticleCheckFailureMessage(this.message);
  }

  get isInteractive () {
    return ItemType.isInteractiveMessage(this.message);
  }

  get interactivePart1 () {
    return getInteractivePart(this.message, 0);
  }

  get interactivePart2 () {
    return getInteractivePart(this.message, 1);
  }

  get isListMessage () {
    return ItemType.isListMessage(this.message);
  }

  get isTypingMessage () {
    return ItemType.isTypingMessage(this.message);
  }

  get isResolutionMessage () {
    return ItemType.isResolutionMessage(this.message);
  }

  get showNoneOfTheAbove () {
    return ItemType.isListMessage(this.message) && this.message.config?.noneOfTheAbove?.show;
  }

  get isArticleMessage () {
    return ItemType.isArticleMessage(this.message);
  }

  get isChatMessage () {
    return ItemType.isChatMessage(this.message);
  }

  get isIssuesList () {
    return ItemType.isTransactionMessage(this.message);
  }

  get isSystemInteractionMessage () {
    return this.message.type === MessageType.RESOLUTION || this.isListMessage;
  }

  get isMediaMessage () {
    return ItemType.isMediaMessage(this.message);
  }

  get isAlternativeActionsMessage () {
    return ItemType.isAlternativeActionsMessage(this.message);
  }

  get isUserInputRequestForTransaction () {
    return ItemType.isUserInputRequestTransactionMessage(this.message);
  }

  get isUserTransactionSubmissionMessage () {
    return ItemType.isUserTransactionSubmissionMessage(this.message);
  }

  get isAiResponseForUserMessage () {
    return ItemType.isAiMessageForUser(this.message);
  }

  get customize () {
    // Apply classes to the wrappers based on
    // the message type
    return {
      wrapper: {
        'max-w-unset': this.isUserInputRequestForTransaction || this.isUserTransactionSubmissionMessage,
      },
      chatMsg: {
        'max-w-unset': this.isUserInputRequestForTransaction || this.isUserTransactionSubmissionMessage,
        'large-mb': this.isAiResponseForUserMessage,
      },
    };
  }

  get messageText () {
    const { message } = this;
    const isAgentSideChatMessage = message.type === MessageType.CHAT_MESSAGE && message.memberType === MemberType.AGENT;
    const messageWithoutPrefix = removeMessagePrefix(message.message);

    const containsHtml = /<[^>]+>/.test(messageWithoutPrefix);

    if (containsHtml) {
      return {
        render: 'html',
        message: sanitizeHTML(messageWithoutPrefix),
      };
    }

    if (!isAgentSideChatMessage) {
      return {
        render: 'string',
        message: messageWithoutPrefix,
      };
    }

    const messageWithUrls = identifyUrls(messageWithoutPrefix);
    if (messageWithUrls === messageWithoutPrefix) {
      return {
        render: 'string',
        message: messageWithoutPrefix,
      };
    }
    return {
      render: 'html',
      message: messageWithUrls,
    };
  }

  isArticleOption (option: SystemOption) {
    return OptionUtils.isArticleOption(option);
  }

  isSectionOption (option: SystemOption) {
    return OptionUtils.isSectionOption(option);
  }

  sendEvent ($event: BaseEvent) {
    this.$emit('input', $event);
  }

  selectOption (message: BaseSystemMessage, selected: SystemOption) {
    const $event: SystemOptionEvent = {
      message,
      type: SystemEvent.LIST_OPTION_SELECTED,
      payload: { selected },
    };
    this.sendEvent($event);
  }

  selectNoneOfTheAbove (message: BaseSystemMessage) {
    const $event: NoneOfTheAboveEvent = {
      message,
      type: SystemEvent.NONE_OF_THE_ABOVE_SELECTED,
    };
    this.sendEvent($event);
  }

  rollback (message: BaseSystemMessage) {
    const $event: RollbackEvent = {
      message,
      type: SystemEvent.USER_ROLLBACK,
    };
    this.sendEvent($event);
  }

  openArticle (message: SystemArticleMessage) {
    const $event: ArticleExpandedEvent = {
      message,
      type: SystemEvent.ARTICLE_PREVIEW_EXPANDED,
    };
    this.sendEvent($event);
  }
}
